import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash-es";
import classNames from "classnames";

import Modal from "ManagementTeam/Shared/Modal";
import ModalActionBar from "shared/ModalActionBar";
import Button from "shared/Button";
import Input from "shared/@forms/Input";
import { IoIosSearch } from "react-icons/io";
import ArchivedCommitteeMember from "../ArchivedCommitteeMember";

import styles from "./AddCommitteeModal.module.css";
import ModalCard from "ManagementTeam/Shared/ModalCard";
import { Dashboard } from "../../DashboardPage/useDashboard";

/**Modal when adding / editing a committee member  */
export function AddCommitteeModal({
  isOpen,
  onClose,
  onSave,
  committeeMember,
}) {
  const { data: dashboardData, status } = Dashboard.useContainer();
  const [dashboard, setDashboard] = useState([]);
  useEffect(() => {
    setDashboard(dashboardData && dashboardData.meetings ? dashboardData.meetings : [])
  }, [dashboardData]);

  const members = useRef();

  useEffect(() => {
    if (status === "success" && dashboard[0]) {
      members.current = dashboard[0].users;
    }
  }, [dashboard]);

  const [formData, setFormData] = useState({});
  const [searchData, setSearchData] = useState({
    name: "",
    yearStarted: undefined,
  });
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [addedMembers, setAddedMembers] = useState([]);
  const autoCompleteRef = useRef(null);
  const [errors, setErrors] = useState({
    title: "",
    yearStarted: "",
    description: "",
  });

  //Handles closing the autocomplete box when it's clicked outside of
  const useOutsideClick = (ref) => {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowAutoComplete(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };
  useOutsideClick(autoCompleteRef);

  const handleFormData = (e, field) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleNameInput = (e) => {
    if (!e.target.value || e.target.value.trim() == "") {
      setSearchData({
        _id: "",
        name: "",
        yearStarted: "",
        photoUrl: "",
      });

    } else {
      setSearchData({ ...searchData, name: e.target.value });
      debouncedHandleSearchSuggestion(e.target.value);
    }
  };

  const handleSearchSuggestion = (input) => {
    filterMembers(input);
    if (input.length > 0) setShowAutoComplete(true);
    else setShowAutoComplete(false);
  };

  const debouncedHandleSearchSuggestion = useMemo(
    () => debounce(handleSearchSuggestion, 200),
    []
  );

  const filterMembers = (filterItem) => {
    //split in to first and last
    const firstAndLast = members.current.map((member) => {
      let arr = [{
        _id: member._id,
        name: member.profile.firstName + " " + member.profile.lastName,
        photoUrl: member.profile.photoUrl
      }]
      if (member.profileB) {
        arr.push({
          _id: member._id,
          name: member.profileB.firstName + " " + member.profileB.lastName,
          photoUrl: member.profileB.photoUrl
        })
      }
      return arr
    }).flat();


    const filtered = firstAndLast.filter(a => a.name.toUpperCase().includes((filterItem || "").toUpperCase()));

    console.log('filtered', filtered);
    setFilteredMembers(filtered);
  };

  const handleFilterSelection = (selectedIndex) => {
    const member = filteredMembers[selectedIndex];

    setSearchData({
      ...searchData,
      _id: member._id,
      name: member.name,
      photoUrl: member.photoUrl,
    });

    setShowAutoComplete(false);
    
    setErrors({
      members: ""
    });
  }

  useEffect(() => {
    if (committeeMember) {
      setFormData({
        _id: committeeMember._id,
        name: committeeMember.name,
        yearStarted: committeeMember.yearStarted,
        description: committeeMember.description,
      });
      setAddedMembers(committeeMember.members);
    } else {
      setFormData({
        _id: "",
        name: "",
        yearStarted: "",
        description: "",
      });
    }
  }, [committeeMember]);

  const handleAddMember = () => {
    if(!searchData.name || searchData.name.trim() == "") {
      setErrors({ ...errors, members: "Please select a member from the list" });
      return;
    }

    if (!searchData.yearStarted || isNaN(parseInt(searchData.yearStarted)) || searchData.yearStarted.length < 4 || searchData.yearStarted.length > 4) {
      setErrors({ ...errors, membersYear: "Please enter a year" });
      return;
    }
    setErrors({ ...errors, members: "", membersYear: "" });
    const _addedMembers = [
      ...addedMembers,
      {
        _id: searchData._id,
        name: searchData.name,
        yearStarted: parseInt(searchData.yearStarted),
        photoUrl: searchData.photoUrl || "",
      },
    ];
    setAddedMembers(_addedMembers);
    setSearchData({
      _id: "",
      name: "",
      yearStarted: "",
      photoUrl: "",
    });
  };

  const handleDeleteMember = (index) => {
    const _addedMembers = addedMembers.filter((member, i) => i !== index);
    setAddedMembers(_addedMembers);
  };

  const clearFormData = () => {
    setAddedMembers([]);

    setFormData({
      _id: "",
      name: "",
      yearStarted: "",
      description: "",
    });
    
    setErrors({
      title: "",
      yearStarted: "",
      description: "",
      members: "",
      membersYear: ""
    })
  };

  const handleClose = () => {
    clearFormData();
    onClose();
  };

  const objectIsEmpty = (obj) => Object.values(obj).every((value) => value === "");

  const handleSave = () => {
    let tempErrors = {};

    if (formData.name.length === 0) {
      tempErrors = { ...tempErrors, name: "Please enter the committee name" };
    } else {
      tempErrors = { ...tempErrors, name: "" };
    }

    if (formData.yearStarted.length < 4 || formData.yearStarted.length > 4) {
      tempErrors = {
        ...tempErrors,
        yearStarted: "Please enter a year",
      };
    } else {
      tempErrors = { ...tempErrors, yearStarted: "" };
    }

    if (formData.description.length === 0) {
      tempErrors = {
        ...tempErrors,
        description: "Please enter the committee description",
      };
    } else {
      tempErrors = { ...tempErrors, description: "" };
    }

    setErrors(tempErrors);

    if (objectIsEmpty(tempErrors.name) && objectIsEmpty(tempErrors.yearStarted) && objectIsEmpty(tempErrors.description)) {
      onSave(formData, addedMembers);
      clearFormData();
    }
  };

  return (
    <Modal isOpen={isOpen} className={styles.container}>
      <span className={styles.buttonMargins}>
        <ModalActionBar>
          <Button className={styles.cancelButton} onClick={() => handleClose()}>
            Cancel
          </Button>
          <Button
            className={styles.saveButton}
            onClick={() => handleSave(formData, addedMembers)}
          >
            Save
          </Button>
        </ModalActionBar>
      </span>
      <ModalCard>
        <div className={styles.cardContainer}>
          <div className={styles.formContainer}>
            <span className={styles.cell}>
              <span className={styles.label}>Committee Title</span>
              <span className={errors.name ? styles.errorInput : ""}>
                <Input
                  value={formData.name || ""}
                  placeholder={"Committee Title"}
                  onChange={(e) => handleFormData(e, "name")}
                />
              </span>
              {errors.name && (
                <span className={styles.error}>{errors.name}</span>
              )}
            </span>
            <span className={styles.cell}>
              <span className={styles.label}>Year Started</span>
              <span className={errors.yearStarted ? styles.errorInput : ""}>
                <Input
                  value={formData.yearStarted || ""}
                  placeholder={"Year Started"}
                  onChange={(e) => handleFormData(e, "yearStarted")}
                />
              </span>
              {errors.yearStarted && (
                <span className={styles.error}>{errors.yearStarted}</span>
              )}
            </span>
            <span className={styles.cell}>
              <span className={styles.label}>Description of Committee</span>
              <textarea
                className={classNames(
                  styles.textArea,
                  errors.description && styles.errorInput
                )}
                value={formData.description || ""}
                placeholder={"Committee Description"}
                onChange={(e) => handleFormData(e, "description")}
              />
              {errors.description && (
                <span className={styles.error}>{errors.description}</span>
              )}
            </span>
          </div>
          <div className={styles.addMembersContainer}>
            <span className={styles.label}>Add Members</span>
            <hr className={styles.divider} />
            <div className={styles.searchContainer}>
              <div className={styles.searchBar}>
                <IoIosSearch className={styles.searchIcon} />
                <Input
                  placeholder={"Search Committee Members"}
                  value={searchData.name || ""}
                  onChange={(e) => handleNameInput(e)}
                  onFocus={() => {
                    if (searchData.name.length > 0) setShowAutoComplete(true);
                  }}
                />
                {showAutoComplete && (
                  <div className={styles.suggestionBox} ref={autoCompleteRef}>
                    {filteredMembers.map((member, index) => (
                      <span
                        key={index}
                        className={styles.suggestionItem}
                        onClick={() => handleFilterSelection(index)}
                      >
                        {member.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.membersYearContainer}>
                <Input
                  placeholder={"Year Started"}
                  value={searchData.yearStarted || ""}
                  onChange={(e) =>
                    setSearchData({
                      ...searchData,
                      yearStarted: parseInt(e.target.value),
                    })
                  }
                />
                <span
                  className={styles.membersYearErrors}
                >
                  {errors.membersYear && errors.membersYear}
                </span>
              </div>
            </div>
            <span
              className={styles.error}
              style={{ height: "20px", display: "inline-block" }}
            >
              {errors.members && errors.members}
            </span>

            <Button
              className={styles.addMemberButton}
              onClick={() => handleAddMember()}
              disabled={errors.members}
            >
              Add Member
            </Button>
          </div>
          <div className={styles.selectedMemberContainer}>
            {addedMembers &&
              addedMembers.map((member, index) => (
                <span key={index}>
                  <ArchivedCommitteeMember
                    name={member.name}
                    year={member.yearStarted}
                    onDelete={() => handleDeleteMember(index)}
                  />
                </span>
              ))}
          </div>
        </div>
      </ModalCard>
    </Modal>
  );
}

AddCommitteeModal.propTypes = {
  /**Getter to determine if modal is displayed  */
  isOpen: PropTypes.bool,
  /**Callback function for when the modal is closed  */
  onClose: PropTypes.func,
  /**Function called when save is clicked  */
  onSave: PropTypes.func,
  /**Object with information to pre fill the form if the user is editing  */
  committeeMember: PropTypes.shape({
    /**Committee name */
    name: PropTypes.string,
    /**Committee Year */
    year: PropTypes.number,
    /**Committee description */
    description: PropTypes.string,
    /**Array of objects (name - string, year - string) of each committee member */
    members: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        year: PropTypes.number,
      })
    ),
  }),
};
